import React, { Suspense, lazy, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Layout, App as AntApp, Spin } from 'antd';
import { toast } from 'react-toastify';
import { ThemedToastContainer } from './components/ThemedToastContainer';
import 'react-toastify/dist/ReactToastify.css';

// These stay as direct imports (critical path)
import LoginPage from './pages/LoginPage';
import KeycloakCallbackPage from './pages/KeycloakCallbackPage';
import AppSidebar, { SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH } from './components/AppSidebar';
import AppFooter from './components/AppFooter';
import LicenseWarningBanner from './components/LicenseWarningBanner';
import UpdateAvailableBanner from './components/UpdateAvailableBanner';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import { localeFromPath, basenameForLocale } from './i18n/urlLocale';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';
import { useIsAuthenticated, useAuthStore } from './stores/authStore';
import { bootstrapSession } from './api/client';
import { lazyWithRetry } from './utils/lazyWithRetry';
import { HotkeyHelpModal } from './components/HotkeyHelpModal';
import { HotkeyHelpFloatingButton } from './components/HotkeyHelpFloatingButton';
import { useMediaQuery } from './hooks/useMediaQuery';
import { DesktopOnlyGate } from './components/DesktopOnlyGate';
import { MOBILE_MEDIA_QUERY } from './constants/breakpoints';
import './App.css';

// Lazy loaded pages
const ExecutiveReportPage = lazy(() => lazyWithRetry(() => import('./pages/print/ExecutiveReportPage')));
const DeepDiveReportPage = lazy(() => lazyWithRetry(() => import('./pages/print/DeepDiveReportPage')));
const ComplianceReportPage = lazy(() => lazyWithRetry(() => import('./pages/print/ComplianceReportPage')));
const ReportsPage = lazy(() => lazyWithRetry(() => import('./pages/ReportsPage')));
const DashboardPage = lazy(() => lazyWithRetry(() => import('./pages/DashboardPage')));
const InventoryRollupPage = lazy(() => lazyWithRetry(() => import('./pages/InventoryRollupPage')));
const NotFoundPage = lazy(() => lazyWithRetry(() => import('./pages/NotFoundPage')));
const ProjectsPage = lazy(() => lazyWithRetry(() => import('./pages/ProjectsPage')));
const ProjectDetailPage = lazy(() => lazyWithRetry(() => import('./pages/ProjectDetailPage')));
const ProductDetailPage = lazy(() => lazyWithRetry(() => import('./pages/ProductDetailPage')));
const FindingsPage = lazy(() => lazyWithRetry(() => import('./pages/FindingsPage')));
const FindingDetailPage = lazy(() => lazyWithRetry(() => import('./pages/FindingDetailPage')));
const AccessRequestsPage = lazy(() => lazyWithRetry(() => import('./pages/AccessRequestsPage')));
const UsersManagementPage = lazy(() => lazyWithRetry(() => import('./pages/UsersManagementPage')));
const RolesManagementPage = lazy(() => lazyWithRetry(() => import('./pages/RolesManagementPage')));
const PluginsManagementPage = lazy(() => lazyWithRetry(() => import('./pages/PluginsManagementPage')));
const JiraMigrationPage = lazy(() => import('./pages/JiraMigrationPage'));
const ServiceAccountsPage = lazy(() => lazyWithRetry(() => import('./pages/ServiceAccountsPage')));
const AdminTrash = lazy(() => lazyWithRetry(() => import('./pages/AdminTrash')));
const AnalyticsPage = lazy(() => lazyWithRetry(() => import('./pages/AnalyticsPage')));
const LicensePage = lazy(() => lazyWithRetry(() => import('./pages/LicensePage')));
const LLMSettingsPage = lazy(() => lazyWithRetry(() => import('./pages/LLMSettingsPage')));
const JobsPage = lazy(() => lazyWithRetry(() => import('./pages/JobsPage')));
const PerimeterPage = lazy(() => lazyWithRetry(() => import('./pages/PerimeterPage')));
const ProjectScopePage = lazy(() => lazyWithRetry(() => import('./pages/ProjectScopePage')));
const ProfilePage = lazy(() => lazyWithRetry(() => import('./pages/ProfilePage')));

const { Content } = Layout;

const SIDEBAR_COLLAPSED_KEY = 'sidebar_collapsed';

// Public routes render without sidebar chrome regardless of a (possibly stale)
// auth token in localStorage — otherwise LoginPage inherits the sidebar's left
// margin while the redirect-to-dashboard flash is in flight.
// Print report pages are chrome-free (no sidebar/footer) but still require auth.
const PUBLIC_PATHS = ['/login', '/auth/callback'];
const PRINT_PATH_PREFIX = '/reports/print';

interface ShellProps {
  isAuthenticated: boolean;
  isMobile: boolean;
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (v: boolean) => void;
  sidebarWidth: number;
  children: React.ReactNode;
}

// Module-scoped so its component identity is stable across App re-renders —
// otherwise the routed tree would remount and wipe nested state on every
// parent render.
const Shell: React.FC<ShellProps> = ({
  isAuthenticated,
  isMobile,
  sidebarCollapsed,
  setSidebarCollapsed,
  sidebarWidth,
  children,
}) => {
  const location = useLocation();
  const isPrintPage = location.pathname.startsWith(PRINT_PATH_PREFIX);
  const showChrome = isAuthenticated && !PUBLIC_PATHS.includes(location.pathname) && !isPrintPage;

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {showChrome && (
        <AppSidebar collapsed={sidebarCollapsed} onCollapse={setSidebarCollapsed} />
      )}
      <Layout
        style={{
          marginLeft: showChrome ? sidebarWidth : 0,
          transition: 'margin-left 0.2s',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {showChrome && <LicenseWarningBanner />}
        {/* Баннер «доступна новая версия» (#112) — рядом с лицензионным, тем же
            layout-уровнем: оба сообщают о состоянии инсталляции, а не страницы.
            Сам компонент ничего не рендерит, если проверка выключена, обновления
            нет или пользователь не админ (эндпоинт ответит 403). */}
        {showChrome && <UpdateAvailableBanner />}
        <Content
          style={{
            padding: showChrome ? (isMobile ? '64px 16px 24px' : '24px') : '0',
            flex: 1,
          }}
        >
          {children}
        </Content>
        {showChrome && <AppFooter />}
      </Layout>
    </Layout>
  );
};

const PageLoader: React.FC = () => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      justifyContent: 'center',
      alignItems: 'center',
      height: '400px',
    }}
  >
    <Spin size="large" />
    <div>{i18n.t('common:app.pageLoading')}</div>
  </div>
);

const App: React.FC = () => {
  const { t } = useTranslation('common');
  const isAuthenticated = useIsAuthenticated();
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY);
  const [hotkeyHelpOpen, setHotkeyHelpOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true'
  );

  // INIT-GATE: the access token lives only in memory, so a page reload starts
  // with no token. Attempt one silent refresh against the HttpOnly cookie. While
  // it is in flight we render a loading gate (NOT the login page) so a valid
  // session survives reloads. bootstrapSession never throws: on no/expired
  // cookie it resolves to "not authenticated" and the app renders normally
  // (ProtectedRoute then redirects to /login — no infinite spinner).
  const [bootstrapped, setBootstrapped] = useState(
    () => !!useAuthStore.getState().token
  );

  React.useEffect(() => {
    if (bootstrapped) return;
    let cancelled = false;
    void bootstrapSession().finally(() => {
      if (!cancelled) setBootstrapped(true);
    });
    return () => {
      cancelled = true;
    };
  }, [bootstrapped]);

  useHotkeys('f1', () => setHotkeyHelpOpen(true), { preventDefault: true });

  React.useEffect(() => {
    const handleOffline = () => {
      toast.error(t('app.offline'), {
        toastId: 'offline-notification',
        autoClose: false,
      });
    };

    const handleOnline = () => {
      toast.dismiss('offline-notification');
      toast.success(t('app.online'), { autoClose: 3000 });
    };

    if (!navigator.onLine) handleOffline();

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
      toast.dismiss('offline-notification');
    };
  }, [t]);

  const sidebarWidth = isMobile
    ? 0
    : sidebarCollapsed
      ? SIDEBAR_COLLAPSED_WIDTH
      : SIDEBAR_WIDTH;

  // Hold the app behind a spinner until the silent-refresh attempt resolves.
  if (!bootstrapped) {
    return (
      <AntApp>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
          }}
        >
          <Spin size="large" />
        </div>
      </AntApp>
    );
  }

  return (
    <AntApp>
      {/* Заглушка для узких экранов (#322) — ВНУТРИ AntApp (ей нужна тема), но
          снаружи роутера: она не про страницу, а про то, что на этой ширине не
          работает ни одна из них. Пользователь может пройти дальше сам. */}
      <DesktopOnlyGate>
      <BrowserRouter basename={basenameForLocale(localeFromPath())} future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Shell
          isAuthenticated={isAuthenticated}
          isMobile={isMobile}
          sidebarCollapsed={sidebarCollapsed}
          setSidebarCollapsed={setSidebarCollapsed}
          sidebarWidth={sidebarWidth}
        >
          <Suspense fallback={<PageLoader />}>
            <Routes>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/auth/callback" element={<KeycloakCallbackPage />} />
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute>
                        <Navigate to="/dashboard" replace />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <DashboardPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/profile"
                    element={
                      <ProtectedRoute>
                        <ProfilePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/projects"
                    element={
                      <ProtectedRoute>
                        <ProjectsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/projects/:id"
                    element={
                      <ProtectedRoute>
                        <ProjectDetailPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/products/:id"
                    element={
                      <ProtectedRoute>
                        <ProductDetailPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/products/:id/findings"
                    element={
                      <ProtectedRoute>
                        <FindingsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/findings/:id"
                    element={
                      <ProtectedRoute>
                        <FindingDetailPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/inventory"
                    element={
                      <ProtectedRoute>
                        <InventoryRollupPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/analytics"
                    element={
                      <ProtectedRoute>
                        <AnalyticsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/access-requests"
                    element={
                      <ProtectedRoute>
                        <AccessRequestsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/trash"
                    element={
                      <AdminRoute>
                        <AdminTrash />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/admin/users-management"
                    element={
                      <AdminRoute>
                        <UsersManagementPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/admin/roles-management"
                    element={
                      <AdminRoute>
                        <RolesManagementPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/admin/jira-migration"
                    element={
                      <AdminRoute>
                        <JiraMigrationPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/admin/plugins-management"
                    element={
                      <AdminRoute>
                        <PluginsManagementPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/service-accounts"
                    element={
                      <ProtectedRoute>
                        <ServiceAccountsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings/license"
                    element={
                      <AdminRoute>
                        <LicensePage />
                      </AdminRoute>
                    }
                  />
                  {/* Настройки LLM (#198) — AdminRoute, как и лицензия: они
                      описывают, каким движком инсталляция выносит вердикты. */}
                  <Route
                    path="/settings/llm"
                    element={
                      <AdminRoute>
                        <LLMSettingsPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/jobs"
                    element={
                      <AdminRoute>
                        <JobsPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/perimeter"
                    element={
                      <ProtectedRoute>
                        <PerimeterPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/projects/:id/scope"
                    element={
                      <ProtectedRoute>
                        <ProjectScopePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports"
                    element={
                      <ProtectedRoute>
                        <ReportsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports/print/company"
                    element={
                      <ProtectedRoute>
                        <ExecutiveReportPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports/print/compliance"
                    element={
                      <ProtectedRoute>
                        <ComplianceReportPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports/print/project/:id"
                    element={
                      <ProtectedRoute>
                        <DeepDiveReportPage kind="project" />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports/print/product/:id"
                    element={
                      <ProtectedRoute>
                        <DeepDiveReportPage kind="product" />
                      </ProtectedRoute>
                    }
                  />
                  {/* Catch-all — ОБЯЗАТЕЛЬНО последним: React Router выбирает
                      наиболее конкретный маршрут, но держать `*` в конце
                      важно для читателя, иначе список выглядит оборванным.
                      Без него неизвестный адрес давал пустой экран без единого
                      слова (#320). Внутри ProtectedRoute — чтобы неизвестный
                      адрес у неавторизованного вёл на вход, а не сообщал
                      анониму, что «страницы нет», раскрывая наличие
                      приложения. */}
                  <Route
                    path="*"
                    element={
                      <ProtectedRoute>
                        <NotFoundPage />
                      </ProtectedRoute>
                    }
                  />
          </Routes>
        </Suspense>
        </Shell>
      </BrowserRouter>
      </DesktopOnlyGate>
      <ThemedToastContainer
        position="bottom-right"
        autoClose={10000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        limit={10}
      />
      <HotkeyHelpModal open={hotkeyHelpOpen} onClose={() => setHotkeyHelpOpen(false)} />
      {isAuthenticated && <HotkeyHelpFloatingButton />}
    </AntApp>
  );
};

export default App;
